import { httpRequest } from '@lib';
import { useQuery } from '@tanstack/react-query';
import { ApiEndPoints } from '@constants';
import { useProfileSelectionActions } from '@app/profile-selection-store';

// A principal group carries several identifiers ({ idType, cpId }); the primary
// one is what downstream cpId-keyed endpoints and the EUP request payload use.
export const getPrimaryIdentifier = (group) => group?.primaryIdentifier || group?.identifiers?.[0] || null;
export const getPrimaryCpId = (group) => getPrimaryIdentifier(group)?.cpId || null;

export const findPrincipalGroupById = (records, principalGroupId) => {
  let match = null;
  if (principalGroupId && Array.isArray(records)) {
    match = records.find((group) => group?.principalGroupId === principalGroupId) || null;
  }
  return match;
};

// When a principalGroupId is given (deep link / reload), restore the selection
// into the store from the fetched list.
const fetchProfileSelection = async (principalGroupId) => {
  const {
    setPrincipalGroupId, setPrimaryIdentifier, setCpId, setCpName,
  } = useProfileSelectionActions();
  try {
    const { data } = await httpRequest.get(ApiEndPoints.FETCH_PRINCIPAL_GROUPS);

    if (principalGroupId) {
      const group = findPrincipalGroupById(data?.data?.records, principalGroupId);
      setPrincipalGroupId(group?.principalGroupId || null);
      setPrimaryIdentifier(getPrimaryIdentifier(group));
      setCpId(getPrimaryCpId(group));
      // cpName holds only a real display name: the request form prefills the
      // Name field from it, so a cpId must not masquerade as a name. Sidebar and
      // topbar fall back to cpId themselves.
      setCpName(group?.displayName || null);
    }
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

export const useFetchProfileSelectionQuery = (principalGroupId, { enabled } = {}) => useQuery({
  queryKey: ['PROFILE_SELECTION', principalGroupId],
  queryFn: () => fetchProfileSelection(principalGroupId),
  enabled: enabled ?? !!principalGroupId,
});
